import { Link, useParams } from 'react-router-dom';
import { Chip, Empty, ErrorNote, Icon, PageHeader, Segmented, SectionTitle, Spinner } from '../app/ui';
import { ageLabel } from '../lib/format';
import { usePref } from '../lib/persist';
import { useApi } from '../lib/useApi';
import type { ProjectDetail, ProjectRow } from '../lib/types';
import { SessionCard } from './Now';

type Filter = 'active' | 'parked' | 'all';

export function stageTone(stage: string | null | undefined): 'green' | 'accent' | 'neutral' | 'orange' {
  switch (stage) {
    case 'prod': case 'live': case 'monetized': case 'selling': return 'green';
    case 'uat': case 'beta': case 'testflight': return 'accent';
    case 'idea': case 'parked': return 'neutral';
    default: return 'orange';
  }
}

function Detail({ slug }: { slug: string }) {
  const q = useApi<ProjectDetail>('project', { slug });
  const d = q.data;
  if (q.loading && !d) return <Spinner label="Loading…" />;
  if (!d) return <ErrorNote text={q.error} />;
  const open = d.initiatives.filter((i) => i.status === 'open');
  return (
    <div className="stack">
      <div className="row wrap gap">
        <h2 className="grow">{d.project.name}</h2>
        <Chip tone={stageTone(d.project.stage)}>{(d.project.stage ?? '—').toUpperCase()}</Chip>
        {d.project.trackerApp ? <Chip>{d.project.trackerApp}</Chip> : null}
      </div>
      {d.project.summary ? <p className="muted">{d.project.summary}</p> : null}
      {d.approvals.length ? (
        <>
          <SectionTitle count={d.approvals.length}>Pending approvals</SectionTitle>
          {d.approvals.map((a) => (
            <div key={a.id} className="row gap"><Chip tone="orange">{a.kind ?? 'approval'}</Chip><span className="grow">{a.title}</span><span className="muted small">{ageLabel(a.createdAt)}</span></div>
          ))}
        </>
      ) : null}
      <SectionTitle count={open.length}>Initiatives</SectionTitle>
      {!d.initiatives.length ? <Empty>No initiatives yet.</Empty> : null}
      {d.initiatives.map((i) => (
        <div key={i.id} className="row gap top">
          <span className={`status-mark st-${i.status ?? 'open'}`}>{i.status === 'done' ? '✓' : i.status === 'open' ? '○' : '‖'}</span>
          <div className="grow">
            <div className={i.status === 'done' ? 'strike' : ''}>{i.title}</div>
            <div className="row wrap gap muted small">
              {i.key ? <Chip>{i.key}</Chip> : null}{i.rec ? <Chip>{i.rec}</Chip> : null}
              {i.waitingOn ? <span>waiting on {i.waitingOn}</span> : null}
              {i.trackerKey ? <span className="mono">{i.trackerKey}</span> : null}
            </div>
          </div>
        </div>
      ))}
      {d.sessions.length ? (
        <>
          <SectionTitle count={d.sessions.length}>Recent sessions</SectionTitle>
          <div className="stack">{d.sessions.map((s) => <SessionCard key={s.id} s={s} />)}</div>
        </>
      ) : null}
    </div>
  );
}

export function ProjectsPage() {
  const { slug } = useParams();
  const [filter, setFilter] = usePref<Filter>('projects.filter', 'active');
  const q = useApi<{ projects: ProjectRow[] }>('projects');
  const rows = (q.data?.projects ?? []).filter((p) => filter === 'all' || p.status === filter);

  return (
    <section className={`page split${slug ? ' has-detail' : ''}`}>
      <div className="split-list">
        <PageHeader title="Projects">
          <button className="icon-btn" onClick={() => void q.reload()} aria-label="Refresh"><Icon name="refresh" /></button>
        </PageHeader>
        <Segmented label="Status" value={filter} onChange={setFilter}
          options={[{ id: 'active', label: 'Active' }, { id: 'parked', label: 'Parked' }, { id: 'all', label: 'All' }]} />
        <ErrorNote text={q.error} />
        {q.loading && !q.data ? <Spinner label="Loading…" /> : null}
        <ul className="plist">
          {rows.map((p) => (
            <li key={p.id}>
              <Link to={`/projects/${p.slug}`} className={`prow${p.slug === slug ? ' active' : ''}`}>
                <Chip tone={stageTone(p.stage)}>{(p.stage ?? '—').toUpperCase()}</Chip>
                <span className="grow">
                  <span className="pname">{p.name}</span>
                  <span className="muted small">{[p.kind, p.customer].filter(Boolean).join(' · ')}</span>
                </span>
                {p.waiting ? <Chip tone="orange">{p.waiting} waiting</Chip> : null}
                {p.openInitiatives ? <span className="muted small">{p.openInitiatives} open</span> : null}
              </Link>
            </li>
          ))}
        </ul>
        {q.data && !rows.length ? <Empty>No projects in this view.</Empty> : null}
      </div>
      <div className="split-detail">
        {slug ? (
          <>
            <Link to="/projects" className="back">‹ Projects</Link>
            <Detail key={slug} slug={slug} />
          </>
        ) : <Empty>Select a project</Empty>}
      </div>
    </section>
  );
}
